import React from 'react';
import { View, Text, StyleSheet, useWindowDimensions } from 'react-native';

interface Props {
  label: string;
  value: string | number;
  unit?: string;
  subtitle?: string;
  delta?: number;           // positive = improvement (caller decides direction via lowerIsBetter)
  deltaLabel?: string;
  lowerIsBetter?: boolean;  // when true, positive delta shows ↓ green (fewer = better)
  accent?: string;
  children?: React.ReactNode;
}

export default function TrendKpiCard({
  label, value, unit, subtitle, delta, deltaLabel, lowerIsBetter = false, accent = '#1C60A5', children,
}: Props) {
  const { width } = useWindowDimensions();
  const isNarrow = width < 480;
  const isImprovement = delta !== undefined && delta >= 0;
  const deltaColor = isImprovement ? '#2E7D32' : '#C62828';
  // For lowerIsBetter (e.g. resolution days): improvement = ↓, worsening = ↑
  // For default (higher is better): improvement = ▲, worsening = ▼
  const deltaIcon = lowerIsBetter
    ? (isImprovement ? '↓' : '↑')
    : (isImprovement ? '▲' : '▼');
  return (
    <View style={[styles.card, { borderLeftColor: accent }]}>
      <Text style={styles.label}>{label}</Text>
      <View style={styles.valueRow}>
        <Text style={[styles.value, isNarrow && styles.valueNarrow]}>{value}</Text>
        {unit ? <Text style={styles.unit}> {unit}</Text> : null}
      </View>
      {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
      {delta !== undefined && (
        <View style={styles.deltaRow}>
          <Text style={[styles.deltaIcon, { color: deltaColor }]}>{deltaIcon}</Text>
          <Text style={[styles.deltaText, { color: deltaColor }]}>
            {Math.abs(delta)}% {deltaLabel ?? 'from last period'}
          </Text>
        </View>
      )}
      {children ? <View style={styles.chartSlot}>{children}</View> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FAFAFA',
    borderRadius: 8,
    padding: 14,
    borderLeftWidth: 4,
    flex: 1,
    minWidth: 150,
    marginVertical: 4,
  },
  label: {
    fontSize: 10,
    fontWeight: '700',
    color: '#777',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  value: {
    fontSize: 30,
    fontWeight: '900',
    color: '#111',
    lineHeight: 36,
  },
  valueNarrow: {
    fontSize: 22,
    lineHeight: 26,
  },
  unit: {
    fontSize: 13,
    color: '#555',
    fontWeight: '500',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 11,
    color: '#999',
    marginTop: 3,
  },
  deltaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 5,
    gap: 3,
  },
  deltaIcon: {
    fontSize: 9,
  },
  deltaText: {
    fontSize: 11,
    fontWeight: '600',
  },
  chartSlot: {
    marginTop: 10,
  },
});
